import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Chip,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  FlightTakeoff,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  WhatsApp,
  Refresh,
  ConfirmationNumber,
  ArrowForward,
  Logout,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { HeaderV2, FooterV2 } from '../../components/layout';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://rehman-travels-api.onrender.com/api';

const statusConfig = {
  PriceConfirmed: { label: 'Price Confirmed', color: 'success', icon: <CheckCircle fontSize="small" /> },
  Booked: { label: 'Ticket Booked', color: 'success', icon: <ConfirmationNumber fontSize="small" /> },
  Contacted: { label: 'In Review / Contacted', color: 'primary', icon: <HourglassEmpty fontSize="small" /> },
  Submitted: { label: 'Submitted', color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
  Cancelled: { label: 'Cancelled', color: 'error', icon: <Cancel fontSize="small" /> },
};

const DashboardPageV2 = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('accessToken') || localStorage.getItem('userToken');
  const storedUser = localStorage.getItem('user');
  let user = {};
  try {
    user = storedUser ? JSON.parse(storedUser) : {};
  } catch {
    user = {};
  }

  const loadData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const quotesPromise = axios
        .get(`${API_BASE_URL}/flights/quote-requests/my-quotes`, { headers })
        .then((res) => (Array.isArray(res.data) ? res.data : []))
        .catch((err) => {
          console.warn('Could not fetch quotes:', err);
          return [];
        });

      const bookingsPromise = axios
        .get(`${API_BASE_URL}/bookings/my-bookings`, { headers })
        .then((res) => (Array.isArray(res.data) ? res.data : []))
        .catch((err) => {
          console.warn('Could not fetch bookings:', err);
          return [];
        });

      const [quotesData, bookingsData] = await Promise.all([quotesPromise, bookingsPromise]);
      setQuotes(quotesData);
      setBookings(bookingsData);
    } catch (err) {
      setError('Unable to load some dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/v2/login', {
        state: { info: 'Please sign in to access your customer dashboard.' },
      });
      return;
    }
    loadData();
  }, [token, navigate, loadData]);

  const handleSignOut = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/v2/login');
  };

  const confirmedQuotesCount = quotes.filter((q) => q.status === 'PriceConfirmed' || q.status === 'Booked').length;

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Welcome Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #0b2239 0%, #1e4d79 100%)',
            color: '#fff',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              Welcome, {user.firstName || user.name || user.email || 'Traveler'}!
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              Track your flight fare quotations, agent responses, and bookings in one place.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={loadData}
              startIcon={<Refresh />}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSignOut}
              startIcon={<Logout />}
            >
              Sign Out
            </Button>
          </Stack>
        </Paper>

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, p: 2 }}>
              <Typography variant="body2" color="text.secondary">Flight Quotations</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{quotes.length}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, p: 2 }}>
              <Typography variant="body2" color="text.secondary">Confirmed Fares</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'success.main' }}>
                {confirmedQuotesCount}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, p: 2 }}>
              <Typography variant="body2" color="text.secondary">Package Bookings</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'primary.main' }}>
                {bookings.length}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
            <Tab label={`Flight Quotations (${quotes.length})`} />
            <Tab label={`Package Bookings (${bookings.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tab 0: Flight Quotes */}
            {tabIndex === 0 && (
              <Box>
                {quotes.length === 0 ? (
                  <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <FlightTakeoff sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      No flight quotation requests yet
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Search flights to request live agent portal prices with direct assistance.
                    </Typography>
                    <Button component={RouterLink} to="/v2/flights" variant="contained">
                      Search Flights Now
                    </Button>
                  </Paper>
                ) : (
                  <Stack spacing={2.5}>
                    {quotes.map((q, idx) => {
                      const quoteId = q.id || q.requestId || `quote-${idx}`;
                      const idPrefix = (q.id || q.requestId || '').substring(0, 8) || `Q${idx + 1}`;
                      const cfg = statusConfig[q.status] || {
                        label: q.status || 'Pending',
                        color: 'default',
                        icon: null,
                      };
                      const phoneParam = q.customerPhone ? `&phone=${encodeURIComponent(q.customerPhone)}` : '';
                      const trackingUrl = `/v2/flights/track?requestId=${quoteId}${phoneParam}`;
                      const waMsg = encodeURIComponent(
                        `Hi Rehman Travels, I am inquiring about my flight quote request ${quoteId} for ${q.origin || ''} to ${q.destination || ''}.`
                      );
                      const waLink = `https://wa.me/923000778844?text=${waMsg}`;
                      const confirmedVal = q.confirmedPrice || q.confirmedAmount;
                      const agentNotesText = q.agentMessage || q.agentNotes || q.notes;

                      return (
                        <Card key={quoteId} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              spacing={1}
                              sx={{ mb: 2 }}
                            >
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Request #{idPrefix} · Submitted {q.createdAt ? new Date(q.createdAt).toLocaleDateString() : 'recently'}
                                </Typography>
                                <Typography variant="h6" fontWeight={700}>
                                  {q.origin} ➔ {q.destination}
                                </Typography>
                              </Box>
                              <Chip
                                icon={cfg.icon}
                                label={cfg.label}
                                color={cfg.color}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Stack>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary">Flight</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {q.airlineName || 'Airline'} {q.flightNumber || ''}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary">Departure Date</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {q.departureDate || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary">Indicative Fare</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {q.shownCurrency} {q.shownAmount ? Number(q.shownAmount).toLocaleString() : 'N/A'}
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Confirmed price notification */}
                            {confirmedVal && (
                              <Alert severity="success" sx={{ mb: 2 }}>
                                <strong>Confirmed Agent Price:</strong>{' '}
                                <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                  {q.confirmedCurrency || q.shownCurrency} {Number(confirmedVal).toLocaleString()}
                                </span>
                              </Alert>
                            )}

                            {agentNotesText && (
                              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  Agent Notes:
                                </Typography>
                                <Typography variant="body2">{agentNotesText}</Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                              <Button
                                component="a"
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<WhatsApp />}
                                color="success"
                                variant="outlined"
                                size="small"
                              >
                                WhatsApp Support
                              </Button>
                              <Button
                                component={RouterLink}
                                to={trackingUrl}
                                endIcon={<ArrowForward />}
                                variant="contained"
                                size="small"
                              >
                                View Details & Track
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {/* Tab 1: Bookings */}
            {tabIndex === 1 && (
              <Box>
                {bookings.length === 0 ? (
                  <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <ConfirmationNumber sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      No package bookings found
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Explore our premium and economy Umrah packages tailored to your schedule.
                    </Typography>
                    <Button component={RouterLink} to="/v2/packages" variant="contained">
                      Browse Packages
                    </Button>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {bookings.map((b) => (
                      <Card key={b.id || b.bookingReference} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Ref: {b.bookingReference}
                            </Typography>
                            <Chip label={b.status || 'Active'} color="primary" size="small" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Amount: {b.currency || 'PKR'} {b.totalAmount} · Travelers: {b.numberOfTravelers || 1}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
      <FooterV2 />
    </Box>
  );
};

export default DashboardPageV2;
